import React, { useState } from 'react';
import { Lock, Waves, ArrowRight, Mail, Shield, Users, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { motion } from 'framer-motion';
import { waitlistService } from '../services/waitlistService';

const ComingSoonPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [showPasswordError, setShowPasswordError] = useState(false);
  const [emailError, setEmailError] = useState('');

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === '0ar$') {
      sessionStorage.setItem('glyyd-preview-auth', 'true');
      setShowPasswordError(false);
      // Redirect to the actual landing page
      window.location.href = '/landing';
    } else {
      setShowPasswordError(true);
    }
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    setEmailError('');

    try {
      const result = await waitlistService.addToWaitlist({
        email,
        source: 'coming_soon_page',
      });

      if (result.success) {
        setIsSubscribed(true);
      } else {
        setEmailError(result.error || 'Failed to join waitlist. Please try again.');
      }
    } catch (error) {
      console.error('Error subscribing to waitlist:', error);
      setEmailError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Main Coming Soon Page with integrated password access
  return (
    <div className="min-h-screen bg-gradient-to-br from-rowing-gray-50 to-white">
      {/* Navigation Bar */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-center items-center">
            <div className="flex items-center space-x-2">
              <Waves className="h-8 w-8 text-rowing-blue" />
              <span className="text-2xl font-display font-bold text-rowing-navy">GLYYD</span>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl lg:text-6xl font-display font-bold text-rowing-navy leading-tight">
              The Future of
              <span className="text-rowing-blue block">Rowing Recruitment</span>
            </h1>

            <p className="mt-6 text-xl text-rowing-gray-600 leading-relaxed max-w-3xl mx-auto">
              Connecting high school rowers with college opportunities through data-driven insights
              and streamlined communication. Built by rowers, for rowers.
            </p>

            {/* Launch Timeline */}
            <div className="mt-12 inline-flex items-center space-x-2 bg-rowing-blue/10 px-6 py-3 rounded-full">
              <span className="text-rowing-blue font-semibold">Launching June 2025</span>
              <ArrowRight className="h-4 w-4 text-rowing-blue" />
            </div>
          </motion.div>
        </div>

        {/* Background decoration */}
        <div className="absolute inset-0 -z-10">
          <svg
            className="absolute right-0 top-0 -mt-20 -mr-20 opacity-5"
            width="404"
            height="384"
            fill="none"
            viewBox="0 0 404 384"
          >
            <defs>
              <pattern id="grid" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <rect
                  x="0"
                  y="0"
                  width="2"
                  height="2"
                  fill="currentColor"
                  className="text-rowing-blue"
                />
              </pattern>
            </defs>
            <rect width="404" height="384" fill="url(#grid)" />
          </svg>
        </div>
      </section>

      {/* Email Signup Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-2xl mx-auto text-center"
          >
            <div className="bg-gradient-to-br from-rowing-gray-50 to-white rounded-2xl shadow-xl p-8 lg:p-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-rowing-blue/10 rounded-full mb-6">
                <Mail className="h-8 w-8 text-rowing-blue" />
              </div>

              <h2 className="text-3xl font-display font-bold text-rowing-navy mb-4">
                Be the First to Know
              </h2>

              <p className="text-lg text-rowing-gray-600 mb-8">
                Join our exclusive waitlist and get early access when we launch.
              </p>

              {/* Email Subscription Form */}
              {!isSubscribed ? (
                <form onSubmit={handleSubscribe} className="max-w-md mx-auto">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailError('');
                      }}
                      className={`flex-1 ${emailError ? 'border-rowing-red' : ''}`}
                      required
                    />
                    <Button
                      type="submit"
                      className="bg-rowing-blue hover:bg-rowing-navy text-white px-8"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Joining...' : 'Join Waitlist'}
                    </Button>
                  </div>
                  {emailError && <p className="mt-2 text-sm text-rowing-red">{emailError}</p>}
                  <p className="mt-4 text-sm text-rowing-gray-500">
                    We respect your privacy. No spam, ever.
                  </p>
                </form>
              ) : (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="text-center"
                >
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                    <svg
                      className="w-10 h-10 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-display font-bold text-rowing-navy mb-2">
                    You're on the list!
                  </h3>
                  <p className="text-rowing-gray-600">
                    We'll notify you at{' '}
                    <span className="font-semibold text-rowing-navy">{email}</span> when we launch.
                  </p>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Password Access Section */}
      <section className="py-16 bg-rowing-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="max-w-md mx-auto text-center"
          >
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-rowing-navy/10 rounded-full mb-4">
                <Lock className="h-6 w-6 text-rowing-navy" />
              </div>

              <h3 className="text-xl font-display font-semibold text-rowing-navy mb-2">
                Have an Access Code?
              </h3>

              <p className="text-sm text-rowing-gray-600 mb-4">
                Enter your code to preview the full platform
              </p>

              <form onSubmit={handlePasswordSubmit} className="space-y-3">
                <Input
                  type="password"
                  placeholder="Enter access code"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full ${showPasswordError ? 'border-rowing-red' : ''}`}
                  required
                />
                {showPasswordError && (
                  <p className="text-xs text-rowing-red">Invalid access code</p>
                )}
                <Button
                  type="submit"
                  className="w-full bg-rowing-navy hover:bg-rowing-blue text-white py-2 text-sm"
                >
                  Access Full Site
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Preview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h2 className="text-3xl font-display font-bold text-center text-rowing-navy mb-12">
              What's Coming
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              <motion.div
                whileHover={{ translateY: -4 }}
                className="bg-white rounded-xl shadow-lg p-8 text-center transition-all duration-200"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-rowing-blue/10 rounded-full mb-6">
                  <Users className="h-8 w-8 text-rowing-blue" />
                </div>
                <h3 className="text-xl font-display font-semibold text-rowing-navy mb-3">
                  For Athletes
                </h3>
                <p className="text-rowing-gray-600">
                  Create your profile, track your progress, and connect with college programs that
                  match your goals.
                </p>
              </motion.div>

              <motion.div
                whileHover={{ translateY: -4 }}
                className="bg-white rounded-xl shadow-lg p-8 text-center transition-all duration-200"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-rowing-red/10 rounded-full mb-6">
                  <Shield className="h-8 w-8 text-rowing-red" />
                </div>
                <h3 className="text-xl font-display font-semibold text-rowing-navy mb-3">
                  For Coaches
                </h3>
                <p className="text-rowing-gray-600">
                  Discover talented athletes, manage recruitment efficiently, and build your
                  championship team.
                </p>
              </motion.div>

              <motion.div
                whileHover={{ translateY: -4 }}
                className="bg-white rounded-xl shadow-lg p-8 text-center transition-all duration-200"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
                  <BarChart3 className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-display font-semibold text-rowing-navy mb-3">
                  Data-Driven
                </h3>
                <p className="text-rowing-gray-600">
                  Leverage performance metrics and analytics for informed decisions throughout the
                  recruitment process.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-rowing-navy text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Waves className="h-8 w-8 text-rowing-sky" />
              <span className="text-2xl font-display font-bold">GLYYD</span>
            </div>

            <p className="text-rowing-gray-300 mb-6">
              The future of rowing recruitment is coming soon.
            </p>

            <div className="flex items-center justify-center space-x-6 text-sm">
              <a
                href="/privacy"
                className="text-rowing-gray-400 hover:text-white transition-colors"
              >
                Privacy
              </a>
              <a href="/terms" className="text-rowing-gray-400 hover:text-white transition-colors">
                Terms
              </a>
              <a
                href="/cookies"
                className="text-rowing-gray-400 hover:text-white transition-colors"
              >
                Cookies
              </a>
              <a
                href="mailto:hello@glyyd.net"
                className="text-rowing-gray-400 hover:text-white transition-colors"
              >
                Contact
              </a>
            </div>

            <div className="mt-8 pt-8 border-t border-rowing-gray-700">
              <p className="text-rowing-gray-400 text-sm">
                © {new Date().getFullYear()} Glyyd. All rights reserved. COPPA Compliant.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ComingSoonPage;
